import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import OwnerBadge from "../components/OwnerBadge";
import { fmtDate, fmtMoney } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import { fmtSofiaDate } from "../lib/dashboardDates";
import { ownerLabel } from "../lib/ownerDisplay";
import { ownQuotationsOnly } from "../lib/quotationVisibility";
import { useDashboardScope } from "../hooks/useDashboardScope";
import { reportError } from "../lib/errorReporter";

const TEAM_UNASSIGNED = "unassigned";

const STATUSES = ["draft","sent","opened","question","accepted","declined","expired","archived"];
const ACTIVE_STATUSES = STATUSES.filter((s) => s !== "archived");

// Phase 2C.2 — archive views. `active` is the default so archived records never
// clutter the working list.
const VIEWS = ["active", "archived", "all"];
const DEFAULT_VIEW = "active";

function statusOptionsFor(view) {
  if (view === "archived") return [];
  return view === "all" ? STATUSES : ACTIVE_STATUSES;
}

// Labels for the dashboard-metric active-filter chip.
function metricLabelKey(metric) {
  const map = {
    sent_cohort: "dashboard.kpi_sent_count",
    opened_cohort: "dashboard.kpi_opened_count",
    responded_cohort: "dashboard.kpi_responded_count",
    accepted_cohort: "dashboard.kpi_accepted_from_cohort",
    declined_cohort: "dashboard.kpi_declined_from_cohort",
    accepted_value_period: "dashboard.kpi_accepted_value_period",
    accepted_bucket: "dashboard.section_trend",
    awaiting_response: "dashboard.kpi_awaiting_response",
    expiring_7_days: "dashboard.kpi_expiring_7",
    expired_current: "dashboard.kpi_expired_current",
    active_value: "dashboard.kpi_active_value",
    unassigned_current: "dashboard.kpi_unassigned_current",
    aging_0_3: "dashboard.aging_bucket_0_3",
    aging_4_7: "dashboard.aging_bucket_4_7",
    aging_8_14: "dashboard.aging_bucket_8_14",
    aging_15_plus: "dashboard.aging_bucket_15_plus",
  };
  return map[metric] || null;
}

export default function QuotationsList() {
  const { t } = useTranslation();
  const { orgId, user, currentOrg } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // A member who only ever receives their own quotations is not offered a
  // cross-user filter. The server enforces the scope regardless.
  const ownOnly = ownQuotationsOnly(currentOrg?.role);

  const dashboardMetric = searchParams.get("dashboard_metric");
  const rangeFrom = searchParams.get("from");
  const rangeTo = searchParams.get("to");
  const periodStart = searchParams.get("period_start");
  const periodEnd = searchParams.get("period_end");

  const ownerFilter = searchParams.get("owner_id");
  const mine = searchParams.get("mine") === "true";
  const viewParam = searchParams.get("view");
  const view = VIEWS.includes(viewParam) ? viewParam : DEFAULT_VIEW;
  const urlTeam = searchParams.get("team_id") || null;

  // Phase 2D.3B.3 — the SAME scope read model the dashboard uses: which teams
  // this actor may select, whether the team is locked (a Manager), and which
  // team each selectable person belongs to.
  const { scope } = useDashboardScope(orgId, { enabled: !ownOnly });

  // A Manager reads exactly the team it manages: the scope is server-resolved and
  // read-only, so a team_id in the URL can never redirect it (and a hand-made
  // request naming another team is refused with 403 server-side).
  const teamFilter = useMemo(() => {
    if (ownOnly) return null;
    if (scope.team_locked) return scope.locked_team_id || null;
    return urlTeam;
  }, [ownOnly, scope.team_locked, scope.locked_team_id, urlTeam]);

  const teamByUser = useMemo(() => {
    const index = {};
    (scope.members || []).forEach((m) => {
      if (m.user_id) index[String(m.user_id)] = m.team_id || null;
    });
    return index;
  }, [scope.members]);

  const lockedTeamName = useMemo(() => {
    if (!scope.team_locked) return null;
    const team = (scope.teams || []).find((x) => x.id === scope.locked_team_id);
    return team?.name || null;
  }, [scope.team_locked, scope.locked_team_id, scope.teams]);

  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const dashboardMode = Boolean(dashboardMetric);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = {};
      if (dashboardMode) {
        params.dashboard_metric = dashboardMetric;
        if (rangeFrom) params.from = rangeFrom;
        if (rangeTo) params.to = rangeTo;
        if (periodStart) params.period_start = periodStart;
        if (periodEnd) params.period_end = periodEnd;
      } else {
        if (q) params.q = q;
        // View and status are mutually exclusive on the backend: an explicit
        // status always wins, so only send one of them.
        if (view === "archived") params.archived_only = true;
        else if (view === "all") params.include_archived = true;
        if (status) params.status = status;
      }
      // Owner filter combines with everything; explicit owner_id wins over mine.
      if (ownerFilter) params.owner_id = ownerFilter;
      else if (mine) params.mine = true;
      // Team scope combines with EVERY other filter, in both modes: it is a
      // column predicate on quotations.team_id (migration 28's authoritative
      // attribution), never inferred from current membership.
      if (teamFilter) params.team_id = teamFilter;
      const r = await api.get("/quotations", { params });
      setItems(r.data || []);
    } catch (err) {
      reportError("quotationsList.load", err);
      setLoadError(err?.response?.data?.detail || "error");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [dashboardMode, dashboardMetric, rangeFrom, rangeTo, periodStart, periodEnd, status, q, ownerFilter, mine, view, teamFilter]);

  useEffect(() => { if (orgId) load(); }, [orgId, load]);

  useEffect(() => {
    // Load assignable + inactive-preserving member list once per org for
    // both the filter dropdown and any UI that maps owner_id -> label.
    // A member restricted to its own quotations has no owner filter, so the
    // roster is not fetched for it at all.
    if (!orgId || ownOnly) return;
    api
      .get("/organization/members/assignable")
      .then((r) => setMembers(r.data || []))
      .catch((err) => reportError("quotationsList.loadMembers", err));
  }, [orgId, ownOnly]);

  useEffect(() => {
    const onOrg = () => load();
    window.addEventListener("quowada:orgChanged", onOrg);
    return () => window.removeEventListener("quowada:orgChanged", onOrg);
  }, [load]);

  const clearDashboardFilter = () => {
    const next = new URLSearchParams(searchParams);
    ["dashboard_metric", "from", "to", "period_start", "period_end"].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
  };

  const setView = useCallback(
    (next) => {
      // Switching view resets the status dropdown: its options change with the
      // view, so a stale value would silently contradict the chosen view.
      setStatus("");
      const params = new URLSearchParams(searchParams);
      if (next === DEFAULT_VIEW) params.delete("view");
      else params.set("view", next);
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const setOwnerFilter = useCallback(
    (val) => {
      // val: null (=all), "mine", "unassigned", or a uuid
      const next = new URLSearchParams(searchParams);
      next.delete("owner_id");
      next.delete("mine");
      if (val === "mine") next.set("mine", "true");
      else if (val === "unassigned") next.set("owner_id", "unassigned");
      else if (val) next.set("owner_id", val);
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const activeOwnerFilter = useMemo(() => {
    if (ownerFilter === "unassigned") return "unassigned";
    if (ownerFilter) return ownerFilter;
    if (mine) return "mine";
    return null;
  }, [ownerFilter, mine]);

  // The people offered as a responsible-person filter, narrowed to the SELECTED
  // team: „Без екип" offers the people who belong to no team, a selected team
  // offers its own members, and no team selection offers everybody in scope.
  const ownerCandidates = useMemo(() => {
    if (!teamFilter || !(scope.members || []).length) return members;
    if (teamFilter === TEAM_UNASSIGNED) {
      return members.filter((m) => !teamByUser[String(m.user_id)]);
    }
    return members.filter((m) => teamByUser[String(m.user_id)] === teamFilter);
  }, [members, teamFilter, teamByUser, scope.members]);

  const setTeamFilter = useCallback(
    (val) => {
      // Changing the team drops a selected person who is NOT part of the new
      // team, in the same navigation — the list must never claim to show one team
      // scoped to another team's person.
      const next = new URLSearchParams(searchParams);
      next.delete("team_id");
      if (val) next.set("team_id", val);
      const selected = next.get("owner_id");
      if (selected && selected !== TEAM_UNASSIGNED) {
        const personTeam = teamByUser[String(selected)] || null;
        const compatible =
          !val || (val === TEAM_UNASSIGNED ? personTeam === null : personTeam === val);
        if (!compatible) {
          next.delete("owner_id");
          next.delete("mine");
        }
      }
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams, teamByUser],
  );

  // A team_id left over from another organization (or one this actor may not
  // select) is dropped rather than sent to the API.
  useEffect(() => {
    if (!urlTeam || urlTeam === TEAM_UNASSIGNED) return;
    if (!(scope.teams || []).length) return;
    if (scope.teams.some((team) => team.id === urlTeam)) return;
    const next = new URLSearchParams(searchParams);
    next.delete("team_id");
    setSearchParams(next, { replace: true });
  }, [urlTeam, scope.teams, searchParams, setSearchParams]);

  const metricKey = metricLabelKey(dashboardMetric);
  const metricLabel = metricKey ? t(metricKey) : dashboardMetric;

  const rangeSuffix = useMemo(() => {
    if (!dashboardMode) return "";
    if (periodStart && periodEnd) return ` · ${fmtSofiaDate(periodStart)} – ${fmtSofiaDate(periodEnd)}`;
    if (rangeFrom && rangeTo) return ` · ${fmtSofiaDate(rangeFrom)} – ${fmtSofiaDate(rangeTo)}`;
    return "";
  }, [dashboardMode, periodStart, periodEnd, rangeFrom, rangeTo]);

  const currentUserId = user?.id || null;
  const statusOptions = statusOptionsFor(view);

  return (
    <Layout title={t("quotations.title")} actions={
      <Link className="qw-btn primary" to="/quotations/new" data-testid="btn-new-quotation">+ {t("quotations.new")}</Link>
    }>
      <div className="h1">{t("quotations.title")}</div>
      <div style={{ height: 16 }} />
      {dashboardMode && (
        <div className="hstack" style={{ marginBottom: 12 }}>
          <span className="qw-active-filter" data-testid="dashboard-filter-chip">
            {metricLabel}{rangeSuffix}
            <button
              type="button"
              className="clear"
              onClick={clearDashboardFilter}
              aria-label={t("common.close")}
              data-testid="dashboard-filter-clear"
            >×</button>
          </span>
          <Link to="/dashboard" className="qw-btn ghost sm" data-testid="dashboard-filter-back">← {t("nav.dashboard")}</Link>
        </div>
      )}
      {!dashboardMode && (
        <div className="hstack" style={{ marginBottom: 12 }}>
          <div
            className="qw-seg"
            role="group"
            aria-label={t("quotations.view_label")}
            data-testid="quotations-view-filter"
          >
            {VIEWS.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                aria-pressed={view === v}
                data-testid={`view-filter-${v}`}
              >
                {t(`quotations.view_${v}`)}
              </button>
            ))}
          </div>
          <input
            className="qw-input"
            placeholder={t("common.search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ maxWidth: 280 }}
            data-testid="quotations-search"
          />
          {statusOptions.length > 0 && (
            <select
              className="qw-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{ maxWidth: 200 }}
              aria-label={t("quotations.status")}
              data-testid="quotations-status-filter"
            >
              <option value="">{t("quotations.filter_all")}</option>
              {statusOptions.map((s) => <option key={s} value={s}>{t(`quotations.st.${s}`)}</option>)}
            </select>
          )}
        </div>
      )}

      {/* Phase 2D.3B.3 — the team scope. It combines with every other filter and
          is read-only for a Manager (its own team) and absent for a self-scoped
          actor, which has no team comparison to make. */}
      {!ownOnly && (scope.can_filter_team || scope.team_locked) && (
        <div className="qw-owner-filters" data-testid="quotations-team-filter-row">
          <span style={{ color: "#64748b", fontSize: 12 }}>
            {t("dashboard.scope_team_label")}:
          </span>
          {scope.team_locked ? (
            <span
              className="qw-scope-locked"
              title={t("dashboard.scope_team_locked_hint")}
              data-testid="quotations-team-locked"
            >
              {lockedTeamName || t("dashboard.scope_team_none")}
            </span>
          ) : (
            <select
              className="qw-select"
              value={urlTeam || ""}
              onChange={(e) => setTeamFilter(e.target.value || null)}
              style={{ maxWidth: 240 }}
              aria-label={t("dashboard.scope_team_label")}
              data-testid="quotations-team-filter"
            >
              <option value="">{t("dashboard.scope_team_all")}</option>
              {(scope.teams || []).map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
              {scope.allow_unassigned_team && (
                <option value={TEAM_UNASSIGNED}>
                  {t("dashboard.scope_team_unassigned")}
                </option>
              )}
            </select>
          )}
        </div>
      )}

      {/* Ownership filter chips — hidden for a member who can only ever see
          their own quotations, because every option would promise data the
          server will never return. */}
      {!ownOnly && (
      <div className="qw-owner-filters" data-testid="owner-filter-chips">
        <span style={{ color: "#64748b", fontSize: 12 }}>{t("quotations.owner_column")}:</span>
        <button
          type="button"
          className={`qw-owner-chip ${!activeOwnerFilter ? "active" : ""}`}
          onClick={() => setOwnerFilter(null)}
          data-testid="owner-chip-all"
        >
          {t("dashboard.owner_filter_all")}
        </button>
        <button
          type="button"
          className={`qw-owner-chip ${activeOwnerFilter === "mine" ? "active" : ""}`}
          onClick={() => setOwnerFilter("mine")}
          data-testid="owner-chip-mine"
        >
          {t("quotations.my_quotations")}
        </button>
        <button
          type="button"
          className={`qw-owner-chip ${activeOwnerFilter === "unassigned" ? "active" : ""}`}
          onClick={() => setOwnerFilter("unassigned")}
          data-testid="owner-chip-unassigned"
        >
          {t("quotations.unassigned_quotations")}
        </button>
        <select
          className="qw-select"
          value={
            activeOwnerFilter && activeOwnerFilter !== "mine" && activeOwnerFilter !== "unassigned"
              ? activeOwnerFilter
              : ""
          }
          onChange={(e) => setOwnerFilter(e.target.value || null)}
          style={{ maxWidth: 240 }}
          data-testid="owner-filter-select"
        >
          <option value="">— {t("quotations.filter_owner_placeholder")} —</option>
          {ownerCandidates.map((m) => (
            <option key={m.user_id} value={m.user_id}>
              {ownerLabel(m, t)}
              {m.user_id === currentUserId ? ` (${t("quotations.my_quotations")})` : ""}
            </option>
          ))}
        </select>
      </div>
      )}

      {loading ? (
        <div className="qw-empty">{t("common.loading")}</div>
      ) : loadError ? (
        <div className="qw-card qw-empty" data-testid="quotations-error" style={{ borderColor: "#fecaca", color: "#7f1d1d", background: "#fef2f2" }}>
          {t("common.error_generic")}
        </div>
      ) : items.length === 0 ? (
        <div className="qw-card qw-empty" data-testid="quotations-empty">
          {dashboardMode
            ? t("common.empty")
            : view === "archived"
              ? t("quotations.archived_list_empty")
              : t("quotations.list_empty")}
        </div>
      ) : (
        <div className="qw-table-wrap" tabIndex={0} role="region" aria-label={t("quotations.title")}>
        <table className="qw-table" data-testid="quotations-table">
          <thead>
            <tr>
              <th>{t("quotations.reference")}</th>
              <th>{t("quotations.customer")}</th>
              <th>{t("quotations.owner_column")}</th>
              <th style={{ textAlign: "right" }}>{t("quotations.value")}</th>
              <th>{t("quotations.status")}</th>
              <th>{t("quotations.created")}</th>
              <th>{t("quotations.valid_until")}</th>
              <th>{t("quotations.last_activity")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} data-testid={`quotation-row-${row.reference_number}`}>
                <td>
                  <Link to={`/quotations/${row.id}`} style={{ color: "#0f766e", fontWeight: 600 }}>{row.reference_number}</Link>
                  <div style={{ color: "#64748b", fontSize: 12 }}>{row.title}</div>
                </td>
                <td>{row.customers?.name || "—"}</td>
                <td data-testid={`quotation-owner-${row.reference_number}`}>
                  <OwnerBadge owner={row.owner || null} small />
                </td>
                <td style={{ textAlign: "right" }}>{fmtMoney(row.value, row.currency)}</td>
                <td><StatusBadge status={row.status} /></td>
                <td>{fmtDate(row.created_at)}</td>
                <td>{fmtDate(row.valid_until)}</td>
                <td>{row.last_customer_activity_at ? fmtDate(row.last_customer_activity_at) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </Layout>
  );
}
